import { Link } from 'react-router-dom';
import { useApplications } from '../../hooks/unified/useApplicationsQueries';

interface Application {
  id: string;
  status: string;
  job_title: string;
  job_company?: string;
  applied_at: string;
  status_history?: Array<{
    status: string;
    notes?: string;
    changed_at: string;
  }>;
}

const APPLICATION_STAGES = [
  { key: 'submitted', label: 'Submitted', color: 'blue' },
  { key: 'reviewed', label: 'Reviewed', color: 'indigo' },
  { key: 'shortlisted', label: 'Shortlisted', color: 'purple' },
  { key: 'interviewed', label: 'Interviewed', color: 'pink' },
  { key: 'offered', label: 'Offered', color: 'green' },
  { key: 'hired', label: 'Hired', color: 'emerald' },
];

export function ApplicationProgressCard() {
  const { data: applications, isLoading } = useApplications();

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  // Filter out rejected and withdrawn applications
  const activeApplications = (applications as Application[] | undefined)?.filter(
    (app: Application) => app.status !== 'rejected' && app.status !== 'withdrawn'
  ) || [];

  if (activeApplications.length === 0) {
    return null; // Don't show anything if no active applications
  }

  const getStageIndex = (status: string) => {
    return APPLICATION_STAGES.findIndex((stage) => stage.key === status);
  };

  const getStageColor = (status: string) => {
    const stage = APPLICATION_STAGES.find((s) => s.key === status);
    return stage?.color || 'gray';
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
      <div className="flex items-start sm:items-center justify-between mb-4 sm:mb-6 gap-2">
        <div className="min-w-0 flex-1">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Active Applications</h2>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">
            Track your {activeApplications.length} active job application{activeApplications.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Link
          to="/dashboard/applications"
          className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center whitespace-nowrap min-h-[44px]"
        >
          View All
          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>

      <div className="space-y-4 sm:space-y-6">
        {activeApplications.map((application: Application) => {
          const currentStageIndex = getStageIndex(application.status);
          const stageColor = getStageColor(application.status);

          return (
            <div key={application.id} className="bg-white rounded-lg p-4 sm:p-5 shadow-sm hover:shadow-md transition-shadow">
              {/* Job Info */}
              <div className="flex flex-wrap items-start justify-between gap-2 mb-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{application.job_title}</h3>
                  {application.job_company && (
                    <p className="text-sm text-gray-600 mt-0.5">{application.job_company}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Applied {new Date(application.applied_at).toLocaleDateString()}
                  </p>
                </div>
                <span
                  className={`inline-flex items-center px-2.5 sm:px-3 py-1 rounded-full text-xs font-medium bg-${stageColor}-100 text-${stageColor}-800 flex-shrink-0`}
                >
                  {APPLICATION_STAGES[currentStageIndex]?.label || application.status}
                </span>
              </div>

              {/* Progress Bar */}
              <div className="relative overflow-x-auto pb-2 -mx-2 px-2">
                <div className="min-w-[400px] sm:min-w-0">
                  {/* Background line */}
                  <div className="absolute top-3 sm:top-4 left-2 right-2 sm:left-0 sm:right-0 h-1 bg-gray-200 rounded-full"></div>
                  
                  {/* Progress line */}
                  <div
                    className={`absolute top-3 sm:top-4 left-2 sm:left-0 h-1 bg-${stageColor}-500 rounded-full transition-all duration-500`}
                    style={{ width: `${((currentStageIndex + 1) / APPLICATION_STAGES.length) * 100}%` }}
                  ></div>

                  {/* Stage circles */}
                  <div className="relative flex justify-between">
                    {APPLICATION_STAGES.map((stage, index) => {
                      const isCompleted = index <= currentStageIndex;
                      const isCurrent = index === currentStageIndex;

                      return (
                        <div key={stage.key} className="flex flex-col items-center flex-shrink-0" style={{ width: '60px' }}>
                          <div
                            className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                              isCompleted
                                ? `bg-${stageColor}-500 text-white shadow-lg`
                                : 'bg-gray-200 text-gray-400'
                            } ${isCurrent ? 'ring-2 sm:ring-4 ring-' + stageColor + '-200 scale-110' : ''}`}
                          >
                            {isCompleted ? (
                              <svg className="w-3 h-3 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            ) : (
                              <span className="text-[10px] sm:text-xs font-medium">{index + 1}</span>
                            )}
                          </div>
                          <span
                            className={`text-[10px] sm:text-xs mt-1.5 sm:mt-2 text-center whitespace-nowrap ${
                              isCompleted ? 'text-gray-900 font-medium' : 'text-gray-500'
                            }`}
                          >
                            {stage.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Status History */}
              {application.status_history && application.status_history.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <details className="group">
                    <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-900 flex items-center">
                      <svg
                        className="w-4 h-4 mr-1 transform group-open:rotate-90 transition-transform"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      View History ({application.status_history.length} updates)
                    </summary>
                    <div className="mt-3 ml-5 space-y-2">
                      {application.status_history.slice(0, 3).map((history: any, idx: number) => (
                        <div key={idx} className="text-xs text-gray-600 flex items-start">
                          <span className="w-2 h-2 bg-gray-400 rounded-full mt-1 mr-2"></span>
                          <div>
                            <span className="font-medium">{history.status}</span>
                            {history.notes && <span className="text-gray-500"> - {history.notes}</span>}
                            <span className="text-gray-400 block">
                              {new Date(history.changed_at).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </details>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
